// src/lib/menage-regles-api.js
//
// LECTURE / ÉCRITURE DES RÈGLES D'ASSIGNATION, côté client.
//
// Pas d'endpoint serveur ici, et c'est un choix : la table
// `menage_regles_assignation` porte des policies RLS complètes (SELECT, INSERT,
// UPDATE, DELETE) cloisonnées sur le périmètre de l'orchestrateur, DOUBLÉES
// d'un trigger qui re-vérifie logement ∧ pool et force la trace depuis le jeton.
// La sécurité est donc DANS LA BASE, pas dans un contrôleur qu'on pourrait
// contourner. C'est le même pattern que la lecture des ménages du pool
// (ADR-222) : la RLS fait foi, la vue ne fait que consommer.
//
// ⚠️ Ce qui a JUSTIFIÉ un endpoint service_role ailleurs (incidents, ADR-227)
// n'existe pas ici : là-bas l'orchestrateur n'avait AUCUNE policy SELECT, donc
// un UPDATE RLS-direct renvoyait « 0 ligne » en silence. Ici il voit ses règles.

/**
 * Toutes les règles visibles par l'utilisateur courant.
 * La RLS borne déjà le périmètre — aucun filtre client à ajouter (et surtout
 * pas un filtre qu'on prendrait ensuite pour un droit).
 *
 * @returns {Promise<{ regles: Object, error: string|null }>}
 *   `regles` = index { logement_id → ligne }.
 */
export async function chargerRegles(supabase) {
  const { data, error } = await supabase
    .from('menage_regles_assignation')
    .select('logement_id, provider_email, posee_par, posee_par_role, posee_le')
  if (error) {
    console.error('[regles] chargement', error)
    return { regles: {}, error: error.message }
  }
  const idx = {}
  for (const r of data || []) {
    if (r?.logement_id) idx[r.logement_id] = r
  }
  return { regles: idx, error: null }
}

/**
 * Pose ou remplace la règle d'un logement.
 *
 * On n'envoie JAMAIS `posee_par` / `posee_par_role` / `posee_le` : le trigger
 * les écrase depuis le jeton. Les envoyer donnerait l'illusion qu'ils sont
 * pilotables côté client, et un jour quelqu'un s'appuierait dessus.
 *
 * @returns {Promise<{ ok: boolean, regle: Object|null, error: string|null }>}
 */
export async function poserRegle(supabase, logementId, providerEmail) {
  const email = typeof providerEmail === 'string' ? providerEmail.trim().toLowerCase() : ''
  if (!logementId || !email) {
    return { ok: false, regle: null, error: 'Logement ou agent manquant.' }
  }
  const { data, error } = await supabase
    .from('menage_regles_assignation')
    .upsert({ logement_id: logementId, provider_email: email }, { onConflict: 'logement_id' })
    .select('logement_id, provider_email, posee_par, posee_par_role, posee_le')
  if (error) {
    console.error('[regles] pose', error)
    return { ok: false, regle: null, error: traduire(error) }
  }
  // `.select()` sans ligne = RLS a filtré : l'écriture n'a PAS eu lieu. PostgREST
  // ne lève pas dans ce cas — le confondre avec un succès afficherait une règle
  // qui n'existe pas.
  const ligne = Array.isArray(data) ? data[0] : null
  if (!ligne) {
    return { ok: false, regle: null, error: 'Écriture refusée : logement hors de votre périmètre.' }
  }
  return { ok: true, regle: ligne, error: null }
}

/** Retire la règle : le logement revient à « aucun agent par défaut ». */
export async function retirerRegle(supabase, logementId) {
  if (!logementId) return { ok: false, error: 'Logement manquant.' }
  const { data, error } = await supabase
    .from('menage_regles_assignation')
    .delete()
    .eq('logement_id', logementId)
    .select('logement_id')
  if (error) {
    console.error('[regles] retrait', error)
    return { ok: false, error: traduire(error) }
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { ok: false, error: 'Suppression refusée : logement hors de votre périmètre.' }
  }
  return { ok: true, error: null }
}

/**
 * Les messages du trigger sont écrits pour un journal, pas pour un humain.
 * On les traduit sans les masquer : le message brut reste en console.
 */
function traduire(error) {
  const m = String(error?.message || '')
  if (/hors perimetre|hors périmètre/i.test(m)) return 'Ce logement n’est pas dans votre périmètre.'
  if (/hors pool/i.test(m)) return 'Cet agent ne fait pas partie de votre équipe.'
  if (/role non autorise/i.test(m)) return 'Vous n’avez pas le droit de poser une règle.'
  return 'Enregistrement impossible. Réessayez, puis signalez-le si ça persiste.'
}
